import type { ReactElement, ReactNode } from 'react';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import clsx from 'clsx';
import { endOfDay, format } from 'date-fns';
import { split } from 'lodash';
import DatePicker, { type DatePickerProps } from 'react-datepicker';
import { FormGroup, FormText, Input, InputGroup, Label } from 'reactstrap';
import type { QueryParamConfig } from 'use-query-params';
import { useQueryParam } from 'use-query-params';
import CreatableSelect from 'atom-components/select/creatable-select';
import type {
  ClearIndicatorProps,
  GroupBase,
  GroupProps,
  Props as SelectProps
} from 'atom-components/select/select';
import Select from 'atom-components/select/select';
import {
  commonDateFormat,
  formatDateForAPI,
  isSameOrAfter,
  isSameOrBefore
} from 'helpers/date.helpers';
import type { BasePeriod } from 'services/assist-api/entities/period/periods';
import { DateFilterParam, PageParam, StringFilterParam } from './encoding';

/**
 * Custom clear indicator for react-select
 * - shows number of selected options
 * - styled as a pill
 */
export function ClearIndicator<
  O,
  Multi extends boolean = boolean,
  G extends GroupBase<O> = GroupBase<O>
>({
  innerProps: { ref, ...restInnerProps },
  ...restProps
}: ClearIndicatorProps<O, Multi, G>): ReactElement {
  return (
    <div
      {...restInnerProps}
      ref={ref}
      className={clsx('btn rounded-pill m-1 py-1 px-2 btn-light', {
        'btn-light text-muted': !restProps.isFocused
      })}
    >
      Remove {restProps.getValue()?.length}
      <FontAwesomeIcon icon="circle-xmark" className="ms-2" />
    </div>
  );
}

/**
 * Custom group, enables clicking on the header to add all child options
 */
export function Group<Option, Group extends GroupBase<Option>>({
  options: selectOptions,
  getValue,
  setValue,
  children,
  label
}: GroupProps<Option, true, Group>): ReactElement {
  const onClick = useCallback(() => {
    // TODO the type from react-select don't seem correct here
    const options = selectOptions as unknown as {
      isDisabled: boolean;
      isSelected: boolean;
      data: Option;
    }[];

    setValue(
      [...getValue(), ...options.filter((o) => !o.isDisabled && !o.isSelected).map((o) => o.data)],
      'select-option'
    );
  }, [getValue, selectOptions, setValue]);

  return (
    <>
      <button
        type="button"
        onClick={onClick}
        className="fw-bold m-2 p-2 btn btn-light d-flex align-items-center justify-content-between text-dark"
      >
        <span>{label}</span>
        <FontAwesomeIcon icon="plus-circle" className="ms-4 text-info" />
      </button>
      {children}
    </>
  );
}

/**
 * A filter that allows the user to create an option rather than select an option
 *
 * - value synced to url params
 * - clear button shows count to remove
 * - pill / value needs to be rendered elsewhere
 */
export function CreatableSelectFilter(
  props: Pick<
    SelectProps<unknown, true>,
    'isSearchable' | 'isClearable' | 'closeMenuOnSelect' | 'placeholder' | 'hideSelectedOptions'
  > & {
    name: string;
  }
): ReactElement {
  const { name } = props;

  const [tableName] = split(name, '.');
  const [query, setQuery] = useQueryParam(name, StringFilterParam);
  const [page, setPage] = useQueryParam(`${tableName}.page`, PageParam);

  const selectValue = useMemo(
    () => query?.map((value) => ({ label: value, value })) || [],
    [query]
  );

  return (
    <CreatableSelect<{ label: string; value: string }, true>
      components={{ ClearIndicator }}
      {...props}
      noOptionsMessage={(): string => '↿ Enter your filter above ↾'}
      inputId={name}
      isMulti
      controlShouldRenderValue={false}
      options={selectValue}
      value={selectValue}
      onChange={(option): void => {
        setQuery(option?.length ? option.map(({ value }) => value) : undefined, 'replaceIn');
        setPage({ ...page, pageIndex: 0 }, 'replaceIn');
      }}
    />
  );
}

/**
 * A filter that offers the user a list of potentially grouped options to choose between
 *
 * - value synced to url params
 * - clear button shows count to remove
 * - pill / value needs to be rendered elsewhere
 *
 * - group heading selects all options in that group
 */
export function SelectFilter<
  D,
  OptionType = { label: string; id: number },
  GroupType extends GroupBase<OptionType> = GroupBase<OptionType>
>({
  isGrouped,
  getOptionValue,
  filterParam,
  ...props
}: Pick<
  SelectProps<OptionType, true, GroupType>,
  | 'options'
  | 'isSearchable'
  | 'isClearable'
  | 'hideSelectedOptions'
  | 'getOptionLabel'
  | 'formatOptionLabel'
  | 'formatGroupLabel'
  | 'closeMenuOnSelect'
  | 'placeholder'
  | 'isLoading'
> & {
  name: string;
  getOptionValue(o: OptionType): D;
  isGrouped?: boolean;
  filterParam: QueryParamConfig<D[] | undefined>;
}): ReactElement {
  const [tableName] = split(props.name, '.');
  const [query, setQuery] = useQueryParam(props.name, filterParam);
  const [page, setPage] = useQueryParam(`${tableName}.page`, PageParam);

  const value = isGrouped
    ? (props.options as GroupType[])
        ?.flatMap(({ options }) => options)
        .filter((o) => query?.includes(getOptionValue(o)))
    : (props.options as OptionType[])?.filter((o: OptionType) =>
        query?.includes(getOptionValue(o))
      );

  return (
    <Select<OptionType, true, GroupType>
      isSearchable
      hideSelectedOptions={false}
      components={{ ClearIndicator, Group }}
      {...props}
      getOptionValue={(o): string => String(getOptionValue(o))}
      inputId={props.name}
      isMulti
      value={value}
      controlShouldRenderValue={false}
      onChange={(option): void => {
        setQuery(option.length ? option.map((o) => getOptionValue(o)) : undefined, 'replaceIn');
        setPage({ ...page, pageIndex: 0 }, 'replaceIn');
      }}
    />
  );
}

type DateFieldProps = Omit<
  DatePickerProps,
  'selectsRange' | 'selectsMultiple' | 'showMonthYearDropdown' | 'onChange'
> & {
  name: string;
};

type DateFieldPropsPlus = DateFieldProps & {
  label: ReactNode;
  helperMessage?: string;
  setValue: (date: string) => void;
};

export function DateField({
  label,
  className,
  disabled,
  helperMessage,
  setValue,
  ...props
}: DateFieldPropsPlus): ReactElement {
  return (
    <FormGroup className="col-md-4 col-lg-2">
      <Label htmlFor={props.name}>{label}</Label>
      <InputGroup>
        <div className={clsx('react-datepicker-wrapper')}>
          <DatePicker
            {...props}
            id={props.name}
            className={clsx(className)}
            customInput={<Input />}
            disabled={disabled}
            dateFormat="dd/MM/yyyy"
            timeFormat={undefined}
            onChange={(newValue): void => {
              const coercedDate = props.selectsEnd && newValue ? endOfDay(newValue) : newValue; // if end date set time to end of day
              const processedValue = coercedDate ? formatDateForAPI(coercedDate) : '';
              setValue(processedValue);
            }}
          />
        </div>
      </InputGroup>
      {helperMessage && <FormText>{helperMessage}</FormText>}
    </FormGroup>
  );
}

type Props = {
  name: string;
  periods?: { isLoading: boolean; options: BasePeriod[] };
  minDate?: Date;
  maxDate?: Date;
  className?: string;
  isPeriods?: boolean;
};
export function DateRangeField({
  name,
  periods = { isLoading: false, options: [] },
  minDate,
  maxDate,
  className,
  isPeriods
}: Props): ReactElement {
  const [period, setPeriod] = useState<readonly BasePeriod[]>([]);

  const [{ start, end } = { start: undefined, end: undefined }, setDateRange] = useQueryParam(
    name,
    DateFilterParam
  );

  const startDate = useMemo(() => (start ? new Date(start) : null), [start]);
  const endDate = useMemo(() => (end ? new Date(end) : null), [end]);

  const onChangePeriods = useCallback(
    (period2: BasePeriod) => {
      const pStart = period2?.startDate;
      const pEnd = period2?.endDate;

      setDateRange({ start: pStart, end: pEnd });
      setPeriod(
        periods.options.filter(
          (period3) =>
            isSameOrAfter(new Date(period3.startDate), new Date(pStart)) &&
            isSameOrBefore(new Date(period3.endDate), new Date(pEnd))
        )
      );
    },
    [periods.options, setDateRange]
  );

  useEffect(() => {
    if (isPeriods) {
      setPeriod(
        periods.options.filter(
          (p) =>
            isSameOrAfter(new Date(p.startDate), new Date(start)) &&
            isSameOrBefore(new Date(p.endDate), new Date(end))
        )
      );
    }
  }, [end, isPeriods, periods.options, start]);

  return (
    <>
      {isPeriods && (
        <div className="form-group col-md-4 col-lg-2">
          <label htmlFor={`${name}.period`}>Term</label>
          <Select<BasePeriod, false>
            inputId={`${name}.period`}
            isSearchable
            isClearable
            hideSelectedOptions={false}
            components={{ ClearIndicator }}
            controlShouldRenderValue={false}
            options={periods.options}
            isLoading={periods.isLoading}
            getOptionLabel={(o): string => o.name}
            getOptionValue={(o): string => String(o.id)}
            onChange={onChangePeriods}
            value={period}
          />
        </div>
      )}
      {!isPeriods && (
        <>
          <DateField
            label="Start Date"
            name="startDate"
            className={className}
            selectsStart
            startDate={startDate}
            endDate={endDate}
            minDate={minDate}
            maxDate={maxDate}
            setValue={(date): void => setDateRange({ start: date, end })}
            value={start ? format(new Date(start), commonDateFormat) : undefined}
          />
          <DateField
            label="End Date"
            name="endDate"
            className={className}
            selectsEnd
            startDate={startDate}
            endDate={endDate}
            minDate={startDate}
            maxDate={maxDate}
            setValue={(date): void => setDateRange({ start, end: date })}
            value={end ? format(new Date(end), commonDateFormat) : undefined}
          />
        </>
      )}
    </>
  );
}
